import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, type Signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

import { DiagnosticRating, roundToDecimals } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';

import { PartnerThemeContext } from ':core/context/partner-theme.context';
import { SocialNetworkRatingBoxComponent } from ':shared/components/social-network-diagnostic-result/social-network-rating-box/social-network-rating-box.component';
import { MINIMUM_POST_COUNT_RECOMMENDATION } from ':shared/constants';

@Component({
    selector: 'app-social-network-diagnostic-result',
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        TranslateModule,
        SocialNetworkRatingBoxComponent,
        NgClass,
    ],
    templateUrl: './social-network-diagnostic-result.component.html',
    styleUrl: './social-network-diagnostic-result.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialNetworkDiagnosticResultComponent {
    readonly socialPage = input.required<Diagnostic['instagramPage'] | Diagnostic['facebookPage'] | null>();
    readonly platformKey = input.required<'facebook' | 'instagram'>();
    readonly socialPageDiagnosticRating = input.required<
        | {
              followersDiagnosticRating: DiagnosticRating;
              likesDiagnosticRating: DiagnosticRating;
              postCountDiagnosticRating: DiagnosticRating;
          }
        | undefined
    >();

    private readonly _partnerThemeContext = inject(PartnerThemeContext);
    readonly partnerTheme = this._partnerThemeContext.activeTheme;

    readonly MINIMUM_POST_COUNT_RECOMMENDATION = MINIMUM_POST_COUNT_RECOMMENDATION;

    readonly i18nPrefix = computed(() => `maloupe.diagnostic_result.${this.platformKey()}`);
    readonly keySuffix = computed(() => (this.partnerTheme() ? '_partner' : ''));

    readonly socialNetworkDiagnosticResult = computed(() => {
        if (this.platformKey() === 'instagram') {
            const instagramPage: Diagnostic['instagramPage'] | null = this.socialPage() as Diagnostic['instagramPage'] | null;
            return {
                accountName: instagramPage?.userName,
                followers: instagramPage?.followerCount ?? 0,
                averageLikeCount: roundToDecimals(instagramPage?.averageLikeCount ?? 0, 0),
                postCount: instagramPage?.postCount ?? 0,
                averageFollowerCountForSimilarRestaurants: roundToDecimals(
                    instagramPage?.averageFollowerCountForSimilarRestaurants ?? 0,
                    0
                ),
                averageLikeCountForSimilarRestaurants: roundToDecimals(instagramPage?.averageLikeCountForSimilarRestaurants ?? 0, 0),
                averagePostCountForSimilarRestaurants: roundToDecimals(instagramPage?.averagePostCountForSimilarRestaurants ?? 0, 0),
            };
        }
        const facebookPage: Diagnostic['facebookPage'] | null = this.socialPage();
        return {
            accountName: facebookPage?.name,
            followers: facebookPage?.followerCount ?? 0,
            averageLikeCount: roundToDecimals(facebookPage?.averageLikeCount ?? 0, 0),
            postCount: facebookPage?.postCount ?? 0,
            averageFollowerCountForSimilarRestaurants: roundToDecimals(facebookPage?.averageFollowerCountForSimilarRestaurants ?? 0, 0),
            averageLikeCountForSimilarRestaurants: roundToDecimals(facebookPage?.averageLikeCountForSimilarRestaurants ?? 0, 0),
            averagePostCountForSimilarRestaurants: roundToDecimals(facebookPage?.averagePostCountForSimilarRestaurants ?? 0, 0),
        };
    });
    readonly socialPlatformDisplayName = computed(() => {
        if (this.platformKey() === 'instagram') {
            const instagramPage = this.socialPage() as Diagnostic['instagramPage'] | null;
            return `@${instagramPage?.userName}`;
        }
        const facebookPage: Diagnostic['facebookPage'] | null = this.socialPage();
        return `${facebookPage?.name}`;
    });

    readonly doesYourSocialPlatformAccountPerformBetterThanCompetitors: Signal<boolean> = computed(() => {
        const result = this.socialPageDiagnosticRating();
        if (!result) {
            return false;
        }

        return (
            [result.followersDiagnosticRating, result.likesDiagnosticRating, result.postCountDiagnosticRating].filter(
                (rating) => rating === DiagnosticRating.GOOD
            ).length >= 2
        );
    });
}
